import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { Star } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getReviews, ApiReview, submitReply, updateReply } from '../../api/reviewsService';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import RatingSummary from '../../components/shared/RatingSummary/RatingSummary';
import ReviewItem from '../../components/shared/ReviewItem/ReviewItem';
import ReplyModal from '../../components/shared/ReplyModal/ReplyModal';
import ImageModal from '../../components/shared/ImageModal/ImageModal';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import { DESIGN_TOKENS } from '../../styles/constants';

// Styled components for pagination (matching Questions page)
const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '32px',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'stretch',
  },
});

const PaginationInfo = styled(Typography)({
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.textSecondary,
  lineHeight: DESIGN_TOKENS.typography.lineHeights.relaxed,
  '@media (max-width: 768px)': {
    fontSize: '14px',
    textAlign: 'center',
  },
});

const PaginationButtons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '@media (max-width: 768px)': {
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
});

const PaginationButton = styled(Button)(({ active }: { active: boolean }) => ({
  minWidth: '56px',
  height: '56px',
  borderRadius: '60px',
  fontFamily: DESIGN_TOKENS.typography.fontFamily,
  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
  fontWeight: active ? DESIGN_TOKENS.typography.fontWeights.bold : DESIGN_TOKENS.typography.fontWeights.normal,
  color: DESIGN_TOKENS.colors.text,
  backgroundColor: active ? '#F4DBC5' : 'transparent',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: active ? '#F4DBC5' : '#F1F4F4',
  },
  '@media (max-width: 768px)': {
    minWidth: '48px',
    height: '48px',
    fontSize: '14px',
  },
}));

interface Review {
  id: string;
  date: string;
  rating: number;
  userName: string;
  orderId: string;
  reviewText: string;
  photos?: string[];
  reply?: {
    text: string;
  };
}

// Utility function to format date
const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day} ${month} ${year}`;
};

// Transform API data to component format
const transformReview = (apiReview: ApiReview): Review => ({
  id: apiReview.reviewId,
  date: formatDate(apiReview.timestamp),
  rating: apiReview.rating,
  userName: apiReview.userName,
  orderId: apiReview.orderId,
  reviewText: apiReview.reviewText,
  photos: apiReview.images.length > 0 ? apiReview.images : undefined,
  reply: apiReview.vendorReply ? {
    text: apiReview.vendorReply,
  } : undefined,
});

const Reviews: React.FC = () => {
  const { vendor } = useVendorAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewIdRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // API state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  const itemsPerPage = 10;
  
  // Calculate pagination
  const totalPages = Math.ceil(reviews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReviews = reviews.slice(startIndex, endIndex);

  // Reset to page 1 when reviews change
  useEffect(() => {
    setCurrentPage(1);
  }, [reviews.length]);

  // Fetch reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      if (!vendor?.vendorId) {
        setError('Vendor ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get reviewId from URL query parameter
        const reviewId = searchParams.get('reviewId');
        
        console.log('Fetching reviews:', { vendorId: vendor.vendorId, reviewId });
        
        const response = await getReviews(vendor.vendorId, reviewId || undefined);
        
        console.log('Reviews API response:', response);
          
        if (response && response.success && response.data) {
          const transformedReviews = response.data.map((r) => transformReview(r));
          setReviews(transformedReviews);
          setTotalReviews(response.count || 0);
          setAverageRating(parseFloat(response.averageRating || '0'));
        } else {
          throw new Error(response?.message || 'API returned unsuccessful response');
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        
        // Handle axios errors with better messages
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Unable to connect to the server. Please make sure the API server is running on http://localhost:3002');
          } else if (err.response) {
            setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
          } else if (err.request) {
            setError('No response from server. Please check if the API is running.');
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch reviews');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [vendor?.vendorId, searchParams]);

  // Scroll to review if reviewId is in URL (after reviews are loaded)
  useEffect(() => {
    const reviewId = searchParams.get('reviewId');
    if (reviewId && reviews.length > 0 && !loading) {
      // Scroll to the review after a short delay to allow page to render
      setTimeout(() => {
        const reviewElement = reviewIdRef.current[reviewId];
        if (reviewElement) {
          reviewElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
    }
  }, [reviews, loading, searchParams]);

  const handleReply = (review: Review) => {
    setSelectedReview(review);
    setReplyModalOpen(true);
  };

  const handleEditReply = (review: Review) => {
    setSelectedReview(review);
    setReplyModalOpen(true);
  };

  const handleSubmitReply = async (replyText: string) => {
    if (!vendor?.vendorId || !selectedReview || !replyText.trim()) {
      return;
    }

    try {
      const isEditing = !!selectedReview.reply;
      
      if (isEditing) {
        // Update existing reply
        await updateReply({
          vendorId: vendor.vendorId,
          reviewId: selectedReview.id,
          reply: replyText.trim(),
        });
        toast.success('Reply updated successfully');
      } else {
        // Create new reply
        await submitReply({
          vendorId: vendor.vendorId,
          reviewId: selectedReview.id,
          reply: replyText.trim(),
        });
        toast.success('Reply submitted successfully');
      }

      // Update local state
      setReviews(prevReviews =>
        prevReviews.map(r =>
          r.id === selectedReview.id
            ? {
                ...r,
                reply: { text: replyText.trim() },
              }
            : r
        )
      );

      setReplyModalOpen(false);
      setSelectedReview(null);
    } catch (err) {
      console.error('Error submitting reply:', err);
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data?.message || 'Failed to submit reply');
      } else {
        toast.error('Failed to submit reply. Please try again.');
      }
    }
  };

  const handlePhotoClick = (photos: string[], photoUrl: string) => {
    setSelectedImages(photos);
    setCurrentImageIndex(photos.indexOf(photoUrl));
    setImageModalOpen(true);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => Math.min(prev + 1, selectedImages.length - 1));
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => Math.max(prev - 1, 0));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const hasReviews = reviews.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
        padding: '40px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        '@media (max-width: 1200px)': {
          padding: '32px',
        },
        '@media (max-width: 768px)': {
          padding: '20px',
          gap: '16px',
        },
      }}
    >
      {/* Page Title */}
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '44px',
                fontWeight: 700,
                lineHeight: '50.6px',
                letterSpacing: '-0.44px',
                color: '#181818',
                '@media (max-width: 768px)': {
                  fontSize: '32px',
                  lineHeight: '38px',
                },
              }}
            >
              Reviews
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '22.4px',
              color: '#181818',
              '@media (max-width: 768px)': {
                fontSize: '14px',
                lineHeight: '20px',
              },
            }}
          >
            View ratings and written reviews left by clients.
          </Typography>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '12px',
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Content */}
      {!loading && !error && hasReviews ? (
        <>
          {/* Rating Summary */}
          <RatingSummary averageRating={averageRating} totalReviews={totalReviews} />

          {/* Filter Indicator */}
          {!loading && searchParams.get('reviewId') && (
            <Alert 
              severity="info" 
              sx={{ 
                borderRadius: '12px',
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              action={
                <Button
                  size="small"
                  onClick={() => {
                    setSearchParams(prev => {
                      const newParams = new URLSearchParams(prev);
                      newParams.delete('reviewId');
                      return newParams;
                    });
                  }}
                  sx={{
                    color: DESIGN_TOKENS.colors.primary,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Show All Reviews
                </Button>
              }
            >
              Showing filtered review
            </Alert>
          )}

          {/* Reviews List */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {paginatedReviews.map((review) => (
              <Box
                key={review.id}
                ref={(el: HTMLDivElement | null) => {
                  reviewIdRef.current[review.id] = el;
                }}
              >
                <ReviewItem
                  date={review.date}
                  rating={review.rating}
                  userName={review.userName}
                  orderId={review.orderId}
                  reviewText={review.reviewText}
                  photos={review.photos}
                  reply={review.reply}
                  reviewId={review.id}
                  onReply={() => handleReply(review)}
                onEditReply={() => handleEditReply(review)}
                onPhotoClick={(photoUrl) => handlePhotoClick(review.photos || [], photoUrl)}
              />
              </Box>
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationInfo>
                {reviews.length > 0 
                  ? `${startIndex + 1}–${Math.min(endIndex, reviews.length)} of ${reviews.length} ${searchParams.get('reviewId') ? 'review' : 'reviews'}`
                  : '0 reviews'
                }
              </PaginationInfo>
              <PaginationButtons>
                {/* Previous Button */}
                <PaginationButton 
                  active={false}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  sx={{
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    ←
                  </Box>
                  Prev
                </PaginationButton>

                {/* Page Numbers */}
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <Typography 
                      key={`ellipsis-${index}`} 
                      sx={{ px: 2, color: DESIGN_TOKENS.colors.text }}
                    >
                      ...
                    </Typography>
                  ) : (
                    <PaginationButton
                      key={page}
                      active={currentPage === page}
                      onClick={() => handlePageChange(page as number)}
                    >
                      {page}
                    </PaginationButton>
                  )
                ))}

                {/* Next Button */}
                <PaginationButton 
                  active={false}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  sx={{
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next
                  <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                    →
                  </Box>
                </PaginationButton>
              </PaginationButtons>
            </PaginationContainer>
          )}
        </>
      ) : !loading && !error ? (
        searchParams.get('reviewId') ? (
          <EmptyState
            title="Review not found"
            description={`Couldn't find any review with ID: ${searchParams.get('reviewId')}`}
            icon={<Star sx={{ fontSize: 120, color: '#F4DBC5' }} />}
          />
        ) : (
          <EmptyState
            title="No reviews"
            description="No feedback on your services given to the moment."
            icon={<Star sx={{ fontSize: 120, color: '#F4DBC5' }} />}
          />
        )
      ) : null}

      {/* Reply Modal */}
      {selectedReview && (
        <ReplyModal
          open={replyModalOpen}
          onClose={() => {
            setReplyModalOpen(false);
            setSelectedReview(null);
          }}
          onSubmit={handleSubmitReply}
          reviewData={{
            userName: selectedReview.userName,
            date: selectedReview.date,
            rating: selectedReview.rating,
            reviewText: selectedReview.reviewText,
          }}
          existingReply={selectedReview.reply?.text}
        />
      )}

      {/* Image Modal */}
      <ImageModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        images={selectedImages}
        currentIndex={currentImageIndex}
        onNext={handleNextImage}
        onPrevious={handlePreviousImage}
      />
    </Box>
  );
};

export default Reviews;

